import React, { Fragment, useEffect } from 'react';
import { useNavigate, useParams, useLocation, useSearchParams } from 'react-router-dom';
import { useSignal } from '@preact/signals-react';
import PageForm from '../../templates/PageForm';
import TextEdit from '../../molecules/TextEdit';
import ATable, { ATableHeader } from '../../organisms/ATable';
import { IoMdArrowRoundBack } from 'react-icons/io';
import { IoIosRefresh } from 'react-icons/io';
import { FaFileCode, FaFileExcel, FaTimes } from 'react-icons/fa';
import { FaCheck } from 'react-icons/fa6';
import FieldsModal, { FieldItem } from './FieldsModal';
import NewTooltip from '../../atoms/NewTooltip';
import { exportToExcel } from '../../../services/ExcelExportService';
import PresentationModal from '../../organisms/PresentationModal';
import CopyButton from '../../atoms/CopyButton';
import { JsonView, allExpanded, defaultStyles } from 'react-json-view-lite';
import 'react-json-view-lite/dist/index.css';
import useFetch from '../../../hooks/useFetch';
import PageFooter from '../../molecules/PageFooter';
import TableSkeleton from '../../atoms/TableSkeleton';
import FormSkeleton from '../../atoms/FormSkeleton';
import Tab, { TabProps } from '../../molecules/Tab';
import CardList from '../../organisms/CardList';
import appConfig from '../../../config/app-config';

const DATA_PROVIDER_ENV = appConfig.apis.dataProviderApiEnv;
const API_KEY = DATA_PROVIDER_ENV.key;
const BASE_URL = `${DATA_PROVIDER_ENV.address}/batch-reports`;

// Event color mapping using Map for better performance
const EVENT_COLOR_MAP = new Map<string, string>([
    ['PENDING_REPORT_CREATED', 'bg-yellow-500'],
    ['REPORT_PROCESSING', 'bg-yellow-500'],
    ['REPORT_ERROR', 'bg-red-500'],
    ['REPORT_FAILED', 'bg-red-500'],
    ['REPORT_COMPLETED', 'bg-blue-500'],
]);

const getEventColor = (eventName: string): string => {
    return EVENT_COLOR_MAP.get(eventName) || 'bg-gray-500';
};

interface BatchReportInfo {
    id?: string;
    identification?: string;
    provider?: string;
    createdAt?: string;
    status?: string;
    documents?: number;
    errors?: number;
    success?: number;
    sending?: number;
    statusFormatted?: string;
    reportType?: string;
    batchReportId?: string;
    canUpdateStatus?: boolean;
    mini_uuid: string;
}

interface BatchReportItem {
    document: string;
    createdAt?: string;
    provider?: string;
    status?: string;
    statusFormatted?: string;
    errorMessage?: string | null;
    [key: string]: any;
}

interface ReportItem {
    document: string;
    createdAt?: string;
    updatedAt?: string;
    status?: string;
    statusFormatted?: string;
    errorMessage?: string | null;
    reportType?: string;
    reportTypeId?: string;
    batchReportId?: string;
    attempts?: number;
    maxAttempts?: number;
    events?: Array<{
        eventId: string;
        eventName: string;
        occurredAt: string;
    }>;
}

const BatchReportPageView: React.FC = () => {
    const { id = '', page = '0', pageSize = '100', field = 'document', order = 'ASC' } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();

    const currentTab = searchParams.get('tab') || 'extraction';

    const apiVersion = useSignal('');
    const headers = useSignal<ATableHeader[]>([]);
    const rows = useSignal<any[]>([]);
    const showValueModal = useSignal(false);
    const selectedValue = useSignal<any | null>(null);
    const report = useSignal<BatchReportInfo | null>(null);
    const showFieldsModal = useSignal(false);
    const selectedFields = useSignal<FieldItem[]>([]);
    const loadingFields = useSignal(false);
    const totalPages = useSignal(0);
    const totalElements = useSignal(0);
    const refreshing = useSignal(false);
    const refreshKey = useSignal(0);

    // Reports tab data
    const reports = useSignal<ReportItem[]>([]);
    const reportsTotalPages = useSignal(0);
    const reportsTotalElements = useSignal(0);

    const currentPage = parseInt(page);
    const currentPageSize = parseInt(pageSize);
    const currentField = field;
    const currentOrder = order as 'ASC' | 'DESC';

    const handleBack = () => {
        // Se veio do formulário, vai para listagem
        if (location.state?.fromForm) {
            navigate('/data-provider/batch-report/0/100/createdAt/DESC');
        } else {
            // Senão, volta no histórico
            navigate(-1);
        }
    };

    const handleExport = () => {
        const completedRows = rows.value;
        exportToExcel(headers.value, completedRows, `batch-report-${id}.csv`);
    };

    const handleRefresh = async () => {
        refreshing.value = true;
        try {
            const response = await fetch(`${BASE_URL}/${id}/refresh-status`, {
                method: 'POST',
                headers: {
                    'x-api-key': API_KEY || '',
                    'Content-Type': 'application/json'
                }
            });

            if (response.ok) {
                const data = await response.json();
                if (data && report.value) {
                    // Atualiza os campos com base no payload retornado
                    report.value = {
                        ...report.value,
                        status: data.status,
                        documents: data.successCount + data.errorCount + data.pendingCount,
                        errors: data.errorCount || 0,
                        success: data.successCount || 0,
                        sending: data.pendingCount || 0,
                    };
                }
            } else {
                console.error('Failed to refresh batch status');
            }
        } catch (error) {
            console.error('Error refreshing batch status:', error);
        } finally {
            refreshing.value = false;
        }
    };

    const pageChanged = (p: number) => {
        navigate(`/data-provider/batch-report/view/${id}/${p - 1}/${currentPageSize}/${currentField}/${currentOrder}`, { replace: true });
    };

    const pageSizeChanged = (size: number) => {
        navigate(`/data-provider/batch-report/view/${id}/0/${size}/${currentField}/${currentOrder}`, { replace: true });
    };

    const pageSorted = (sortField: string, sortOrder: 'ASC' | 'DESC') => {
        navigate(`/data-provider/batch-report/view/${id}/${currentPage}/${currentPageSize}/${sortField}/${sortOrder}`, { replace: true });
    };

    const getCssColorByStatus = (status?: string) => {
        if (!status) return 'bg-gray-800';
        const cssClazz: Record<string, string> = {
            PENDING: 'bg-gray-500',
            ERROR: 'bg-red-500',
            COMPLETED: 'bg-green-500',
        };
        return cssClazz[status.toUpperCase()] || 'bg-gray-500';
    };

    const getCssColorBy = (status?: string) => {
        if (status === 'SUCCESS') return 'bg-green-500';
        if (status === 'ERROR') return 'bg-red-500';
        if (status === 'COMPLETED') return 'bg-blue-500';
        return 'bg-yellow-500'; // PENDING
    };

    const getContentByStatus = (status?: string, errorMessage?: string | null) => {
        if (status === 'ERROR' && errorMessage) {
            return (
                <div className="w-[300px]">
                    <span className="font-bold text-red-500">Erro:</span> {errorMessage}
                </div>
            );
        }
        if (status === 'SUCCESS') return 'Consulta realizada com sucesso';
        if (status === 'ERROR') return 'Erro ao consultar';
        if (status === 'COMPLETED') return 'Consulta finalizada';
        return 'Consulta pendente';
    };

    const { isLoading, data, error, isTokenError } = useFetch(`${BASE_URL}/${id}/page-view?page=${currentPage}&pageSize=${currentPageSize}&order=${currentField},${currentOrder}&_t=${refreshKey.value}`, [{ key: 'x-api-key', value: API_KEY }]);

    // Fetch reports data only when on Reports tab
    const reportsUrl = currentTab === 'reports' ? `${BASE_URL}/${id}/reports?page=${currentPage}&pageSize=${currentPageSize}` : '';
    const { isLoading: isLoadingReports, data: reportsData } = useFetch(
        reportsUrl,
        [{ key: 'x-api-key', value: API_KEY }]
    );

    if (isTokenError) navigate('/denied');
    if (error) navigate('/network-error');

    useEffect(() => {
        if (data) {
            apiVersion.value = `${data.apiName} - ${data.apiVersion}`;
            totalPages.value = data.totalPages || 0;
            totalElements.value = data.totalElements || 0;

            const rpt = data.batch || data;
            if (rpt) {
                report.value = {
                    id: rpt.id,
                    provider: rpt.provider,
                    createdAt: rpt.createdAt,
                    status: rpt.status,
                    documents: rpt.documentsCount || 0,
                    errors: rpt.errorCount || 0,
                    success: rpt.successCount || 0,
                    sending: rpt.sendingCount || 0,
                    statusFormatted: rpt.statusFormatted,
                    reportType: rpt.reportType,
                    batchReportId: rpt.batchReportId,
                    canUpdateStatus: rpt.canUpdateStatus,
                    mini_uuid: rpt.mini_uuid,
                    identification: rpt.identification,
                };
            }

            const dynamicHeaders: ATableHeader[] = (data.fields || []).map((h: any, index: number) => ({
                id: h.field || `column-${index}`,
                title: h.header || `column-${index}`,
                field: h.field || `column-${index}`,
                canSort: false,
                cssClass: 'text-center',
            }));

            headers.value = [
                {
                    id: 'document',
                    title: 'Documento',
                    field: 'document',
                    canSort: true,
                    cssClass: 'text-center',
                    width: '150px',
                    fixed: 'left',
                },
                { id: 'createdAt', title: 'Consulta em', canSort: false, cssClass: 'text-center', width: '140px' },
                ...dynamicHeaders,
                {
                    id: 'status',
                    title: 'Status',
                    field: 'status',
                    canSort: false,
                    cssClass: 'text-center',
                    width: '100px',
                    fixed: 'right',
                },
            ];

            rows.value = data.list || data.items || data.reports || [];
        }
    }, [data]);

    // Process reports data
    useEffect(() => {
        if (reportsData) {
            reports.value = reportsData.reports || [];
            reportsTotalPages.value = reportsData.totalPages || 0;
            reportsTotalElements.value = reportsData.totalElements || 0;
        }
    }, [reportsData]);

    const handleTabChange = (tabName: string) => {
        setSearchParams({ tab: tabName });
    };

    const tabItems: TabProps[] = [
        { id: 'extraction', title: 'Extração', index: 0 },
        { id: 'reports', title: 'Relatórios', index: 1 },
    ];

    // Auto-refresh a cada 15 segundos se status não for COMPLETED
    useEffect(() => {
        if (report.value?.status !== 'COMPLETED') {
            const interval = setInterval(() => {
                forceReload();
            }, 15000);

            return () => clearInterval(interval);
        }
    }, [report.value?.status]);

    const renderValue = (value: any) => {
        if (value === null || value === undefined) return '';
        if (typeof value === 'boolean') {
            return value ? (
                <FaCheck className="mx-auto text-green-600 text-xl" />
            ) : (
                <FaTimes className="mx-auto text-red-600 text-xl" />
            );
        }
        if (Array.isArray(value)) {
            const isPrimitive = value.every((v) => ['string', 'number', 'boolean'].includes(typeof v));
            if (isPrimitive) {
                return value.join(', ');
            }
            return (
                <FaFileCode
                    className="mx-auto text-blue-600 cursor-pointer text-xl"
                    onClick={() => {
                        selectedValue.value = value;
                        showValueModal.value = true;
                    }}
                />
            );
        }
        if (typeof value === 'object') {
            return (
                <FaFileCode
                    className="mx-auto text-blue-600 cursor-pointer text-xl"
                    onClick={() => {
                        selectedValue.value = value;
                        showValueModal.value = true;
                    }}
                />
            );
        }
        return value;
    };

    const renderRows = (items: BatchReportItem[]) => {
        if (!items || items.length === 0) {
            return (
                <tr>
                    <td colSpan={headers.value.length} className="py-2 px-2 text-center">
                        <div className="text-2xl font-bold text-gray-500 mt-4">Nenhum registro encontrado...</div>
                    </td>
                </tr>
            );
        }

        return items.map((row, index) => (
            <tr
                key={index}
                className={`${index % 2 === 0 ? 'bg-slate-100' : ''} border-b border-gray-300 hover:bg-slate-300 hover:shadow-md`}
            >
                {headers.value.map((h) => (
                    <td key={h.id} className="py-2 px-2 text-center">
                        {h.id === 'status' ? (
                            <NewTooltip
                                content={getContentByStatus(row.status, row.errorMessage)}
                                alignment="left"
                            >
                                <span
                                    className={`rounded px-1 py-1 text-xs font-bold text-white ${getCssColorBy(row.status)}`}
                                >
                                    {row.statusFormatted || row.status}
                                </span>
                            </NewTooltip>
                        ) : (
                            renderValue((row as any)[h.field || h.id])
                        )}
                    </td>
                ))}
            </tr>
        ));
    };

    const forceReload = () => {
        refreshKey.value = refreshKey.value + 1;
    };

    const renderReportCard = (item: ReportItem, index: number) => {
        return (
            <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow">
                <div className="space-y-2">
                    <div className="flex justify-between items-start">
                        <p className="font-semibold text-gray-900">{item.document}</p>
                        <span
                            className={`rounded px-2 py-1 text-xs font-bold text-white ${getCssColorBy(item.status)}`}
                        >
                            {item.statusFormatted || item.status}
                        </span>
                    </div>

                    <div className="grid grid-cols-3 gap-2 text-sm">
                        <div>
                            <p className="text-gray-500">Criado em</p>
                            <p className="text-gray-900">{item.createdAt || '-'}</p>
                        </div>
                        <div>
                            <p className="text-gray-500">Atualizado em</p>
                            <p className="text-gray-900">{item.updatedAt || '-'}</p>
                        </div>
                        <div>
                            <p className="text-gray-500">Tentativas</p>
                            <p className="text-gray-900">{item.attempts || 0} / {item.maxAttempts || 3}</p>
                        </div>
                    </div>

                    {item.events && item.events.length > 0 && (
                        <div className="flex gap-1 mt-2">
                            {item.events.map((event, idx) => (
                                <NewTooltip
                                    key={idx}
                                    content={
                                        <div className="text-xs whitespace-pre-line">
                                            <div className="font-semibold">{event.eventName}</div>
                                            <div>{event.occurredAt}</div>
                                        </div>
                                    }
                                    alignment="right"
                                >
                                    <div className={`w-3 h-3 rounded-sm ${getEventColor(event.eventName)} cursor-pointer`} />
                                </NewTooltip>
                            ))}
                        </div>
                    )}

                    {item.errorMessage && (
                        <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded">
                            <p className="text-xs text-red-600">{item.errorMessage}</p>
                        </div>
                    )}
                </div>
            </div>
        );
    };

    return (
        <PageForm
            pageTitle="Relatório em lote"
            apiVersion={apiVersion.value}
            isLoading={isLoading || loadingFields.value}
            breadcrumb={[
                { label: 'Relatórios em lote', url: '/data-provider/batch-report/0/100/createdAt/DESC' },
                { label: 'Detalhe do relatório em lote', url: '#' },
            ]}
            controlSlot={
                <div className="flex justify-end">
                    <button
                        className="text-black bg-gray-300 hover:bg-gray-400 px-8 py-2.5 rounded"
                        onClick={handleBack}
                    >
                        <IoMdArrowRoundBack />
                    </button>
                    {report.value?.status !== 'COMPLETED' && (
                        <button
                            onClick={forceReload}
                            className="p-2 ml-2 text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
                            title="Atualizar dados"
                        >
                            <IoIosRefresh size={18} />
                        </button>
                    )}
                    <NewTooltip content="Exportar para Excel" alignment="left">
                        <button
                            type="button"
                            className="text-white bg-blue-500 hover:bg-blue-700 font-medium rounded text-sm px-4 py-2 ml-2 flex items-center"
                            onClick={handleExport}
                        >
                            <FaFileExcel className="mr-2" />
                            Exportar
                        </button>
                    </NewTooltip>

                </div>
            }
        >
            <div className="p-2 space-y-2">
                {isLoading ? (
                    <FormSkeleton />
                ) : (
                    report.value && (
                        <>
                            <div className="grid grid-cols-12 gap-2">
                                <div className="col-span-1">
                                    <TextEdit label="#" value={report.value.id || ''} disabled />
                                </div>
                                <div className="col-span-4">
                                    <TextEdit label="Identificação" value={report.value.identification || ''} disabled />
                                </div>
                                <div className="col-span-3">
                                    <TextEdit label="Tipo de relatório" value={report.value.reportType || ''} disabled />
                                </div>
                                <div className="col-span-2">
                                    <TextEdit label="Provedor" value={report.value.provider || ''} disabled />
                                </div>
                                <div className="col-span-2">
                                    <TextEdit label="Criado em" value={report.value.createdAt || ''} disabled />
                                </div>

                            </div>
                            <div className="grid grid-cols-12 gap-2 mt-1">
                                <div className="col-span-2 inline-block">
                                    <span className='bg-gray-100 text-gray-800 text-xs font-medium me-2 px-2.5 py-0.5 rounded-sm dark:bg-gray-700 dark:text-gray-400 border border-gray-500'>{report.value.documents}</span>
                                    <span className="text-sm text-gray-500">Documentos</span>
                                </div>
                                <div className="col-span-2 inline-block">
                                    <span className='bg-indigo-100 text-indigo-800 text-xs font-medium me-2 px-2.5 py-0.5 rounded-sm dark:bg-gray-700 dark:text-indigo-400 border border-indigo-400'>{report.value.sending}</span>
                                    <span className="text-sm text-gray-500">Processando</span>
                                </div>
                                <div className="col-span-2 inline-block">
                                    <span className='bg-red-100 text-red-800 text-xs font-medium me-2 px-2.5 py-0.5 rounded-sm dark:bg-gray-700 dark:text-red-400 border border-red-400'>{report.value.errors}</span>
                                    <span className="text-sm text-gray-500">Erro</span>
                                </div>
                                <div className="col-span-2 inline-block">
                                    <span className='bg-green-100 text-green-800 text-xs font-medium me-2 px-2.5 py-0.5 rounded-sm dark:bg-gray-700 dark:text-green-400 border border-green-400'>{report.value.success}</span>
                                    <span className="text-sm text-gray-500">Sucesso</span>
                                </div>
                                <div className="col-span-1 col-end-13">
                                    <label className="block text-sm text-gray-500">Status</label>
                                    <span
                                        className={`rounded px-2 py-1 text-xs font-bold text-white ${getCssColorByStatus(report.value.status)}`}
                                    >
                                        {report.value.statusFormatted || report.value.status}
                                    </span>
                                </div>
                            </div>
                        </>
                    )
                )}

                <div className="mt-4">
                    <Tab
                        tabItems={tabItems}
                        tabIndex={currentTab === 'reports' ? 1 : 0}
                        setTabIndex={(index) => handleTabChange(index === 1 ? 'reports' : 'extraction')}
                    />
                </div>

                {currentTab === 'extraction' && (
                    <>
                        {isLoading ? (
                            <TableSkeleton rows={currentPageSize > 10 ? 10 : currentPageSize} />
                        ) : (
                            <ATable
                                headers={headers.value}
                                fieldSorted={currentField}
                                orderSorted={currentOrder}
                                onSort={pageSorted}
                                autoResize={true}
                                marginTop={316}
                                enableFixedColumns={true}
                            >
                                {renderRows(rows.value)}
                            </ATable>
                        )}

                        {!isLoading && (
                            <PageFooter
                                currentPage={currentPage}
                                totalPages={totalPages.value}
                                itemsPerPage={currentPageSize}
                                totalElements={totalElements.value}
                                onPageChange={pageChanged}
                                onItemsPerPageChange={pageSizeChanged}
                                itemsLabel="registros"
                            />
                        )}
                    </>
                )}

                {currentTab === 'reports' && (
                    <>
                        <div
                            className="mt-4 overflow-y-auto border-b border-gray-200"
                            style={{
                                height: `calc(100vh - 320px)`,
                                overflowY: 'auto'
                            }}
                        >
                            {isLoadingReports ? (
                                <TableSkeleton rows={6} />
                            ) : (
                                <CardList
                                    items={reports.value}
                                    renderCard={renderReportCard}
                                    columns={3}
                                    emptyMessage="Nenhum relatório encontrado"
                                />
                            )}
                        </div>

                        {!isLoadingReports && reports.value.length > 0 && (
                            <PageFooter
                                currentPage={currentPage}
                                totalPages={reportsTotalPages.value}
                                itemsPerPage={currentPageSize}
                                totalElements={reportsTotalElements.value}
                                onPageChange={pageChanged}
                                onItemsPerPageChange={pageSizeChanged}
                                itemsLabel="relatórios"
                            />
                        )}
                    </>
                )}
                <PresentationModal
                    id="value-modal"
                    show={showValueModal.value}
                    onClose={() => {
                        showValueModal.value = false;
                        selectedValue.value = null;
                    }}
                    header="Detalhes"
                    footer={<CopyButton data={selectedValue.value} />}
                >
                    <Fragment>
                        <div className="p-4">
                            <div style={{ height: '500px', overflowY: 'auto' }}>
                                {selectedValue.value && (
                                    <JsonView
                                        data={selectedValue.value}
                                        shouldExpandNode={allExpanded}
                                        style={defaultStyles}
                                    />
                                )}
                            </div>
                        </div>
                    </Fragment>
                </PresentationModal>
                <FieldsModal
                    id="fields-modal"
                    show={showFieldsModal.value}
                    fields={selectedFields.value}
                    onClose={() => {
                        showFieldsModal.value = false;
                        selectedFields.value = [];
                    }}
                /* PresentationModal inside handles height */
                />
            </div>
        </PageForm>
    );
};

export default BatchReportPageView;
